import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type { Session, SupabaseClient } from '@supabase/supabase-js';
import {
  entryDecision, isPasswordRecovery, passwordStepUpFresh, readAssurance, readStepUpMethod, signInMethod,
  signedInEmail, stepUpNeed, type StepUpMethod,
} from './lib/auth';
import { createReader, type Organization } from './lib/data';
import { useGuardedMutation } from './lib/mutations';
import type { BrowserRpcName } from './lib/rpc';
import { SignIn } from './components/SignIn';
import { UnlinkedOAuthAccount } from './components/UnlinkedOAuthAccount';
import { StepUp } from './components/StepUp';
import { Brand, OperationsRoom } from './components/OperationsRoom';

export type Guard = (name: BrowserRpcName, action: () => Promise<void>) => Promise<void>;

const AUTH_LOAD_TIMEOUT_MS = 15_000;

function withTimeout<T>(promise: Promise<T>, ms = AUTH_LOAD_TIMEOUT_MS): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('timeout')), ms);
    promise.then(
      (value) => { window.clearTimeout(timer); resolve(value); },
      (error) => { window.clearTimeout(timer); reject(error); },
    );
  });
}

/**
 * Run an action now, or after owner step-up when the RPC requires aal2.
 *
 * Verification belongs to the session, not to the action: once the owner has
 * verified, everything they do afterwards goes through untouched until they
 * sign out. `verified` lets the screen say so, and offer to get it over with
 * at the start rather than in the middle of a command.
 */
function useStepUpGuard(client: SupabaseClient, session: Session | null) {
  const [pending, setPending] = useState<(() => Promise<void>) | null>(null);
  const [pendingMethod, setPendingMethod] = useState<StepUpMethod>('authenticator');
  const [verified, setVerified] = useState(false);
  // The owner's own choice, read at the moment of each protected action so a change applies at once.
  const recentPassword = useCallback(async (): Promise<{ method: StepUpMethod; fresh: boolean }> => {
    const method = await readStepUpMethod(client);
    if (method !== 'password') return { method, fresh: false };
    const { data } = await client.auth.getSession();
    return { method, fresh: passwordStepUpFresh(data.session?.access_token) };
  }, [client]);
  const readVerified = useCallback(async () => {
    if (!session) { setVerified(false); return; }
    setVerified((await readAssurance(client)).current === 'aal2' || (await recentPassword()).fresh);
  }, [client, session, recentPassword]);
  useEffect(() => { void readVerified(); }, [readVerified]);
  const guard: Guard = useCallback(async (name, action) => {
    const password = await recentPassword();
    const need = stepUpNeed(name, await readAssurance(client), password.fresh);
    if (need === 'none') { await action(); return; }
    setPendingMethod(password.method);
    setPending(() => action);
  }, [client, recentPassword]);
  const verify = useCallback(() => {
    void guard('post_owner_command', async () => { await readVerified(); });
  }, [guard, readVerified]);
  const dialog = pending ? (
    <StepUp
      client={client}
      method={pendingMethod}
      email={session?.user.email ?? null}
      onCancel={() => setPending(null)}
      onVerified={() => { const run = pending; setPending(null); void run(); void readVerified(); }}
    />
  ) : null;
  return { guard, dialog, verified, verify };
}

function Bootstrap({ client, session, guard, onCreated }: { client: SupabaseClient; session: Session; guard: Guard; onCreated: () => void }) {
  const [name, setName] = useState('');
  const { submit, busy, error } = useGuardedMutation(client);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void guard('bootstrap_org', async () => {
      const result = await submit('bootstrap_org', { orgName: name.trim(), ownerUserId: session.user.id });
      if (result.ok) onCreated();
    });
  };
  return (
    <main className="centered card auth">
      <Brand />
      <h1>Create your organization</h1>
      <p className="muted">You become its owner. Owner verification is required.</p>
      <form onSubmit={onSubmit} className="stack">
        <label>Organization name<input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} required /></label>
        <button type="submit" className="primary" disabled={busy || !name.trim()}>Create</button>
      </form>
      {error && <p role="alert">{error}</p>}
    </main>
  );
}

export function App({ client, redirectTo, nodeConnection, plannerUrl }: { client: SupabaseClient; redirectTo: string; nodeConnection: { url: string; publishableKey: string }; plannerUrl: string }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [recovery, setRecovery] = useState(false);
  // Set when someone is sent back from a signed-in account that belongs to nothing, so the
  // sign-in screen can say why they are looking at it again.
  const [signInNotice, setSignInNotice] = useState<string | null>(null);
  const [orgs, setOrgs] = useState<readonly Organization[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const reader = useMemo(() => createReader(client), [client]);
  const { guard, dialog, verified, verify } = useStepUpGuard(client, session);

  useEffect(() => {
    void withTimeout(client.auth.getSession()).then(
      ({ data }) => { setSession(data.session); setReady(true); },
      () => {
        setSession(null);
        setSignInNotice('Brunel could not restore the previous session. Sign in again.');
        setReady(true);
      },
    );
    // A recovery session arrives with the browser from the reset email; the owner
    // should be choosing a new password, not walking straight into the room.
    const { data } = client.auth.onAuthStateChange((event, next) => {
      setSession(next);
      // Whatever sent somebody back to the sign-in screen has been answered by their
      // signing in again; the line explaining it does not outlive that.
      if (next) setSignInNotice(null);
      if (isPasswordRecovery(event)) setRecovery(true);
    });
    return () => data.subscription.unsubscribe();
  }, [client]);

  const loadOrgs = useCallback(async () => {
    try {
      const result = await withTimeout(reader.organizations());
      if (result.ok) { setOrgs(result.rows); setLoadError(null); } else setLoadError(result.message);
    } catch {
      setLoadError('Brunel could not load your account within 15 seconds. Retry, or sign out and sign in again.');
    }
  }, [reader]);

  useEffect(() => {
    if (session) void loadOrgs();
    else setOrgs(null);
  }, [session, loadOrgs]);

  if (!ready) return <main className="centered">Loading…</main>;
  if (!session) return <SignIn client={client} redirectTo={redirectTo} notice={signInNotice} />;
  if (recovery) return <SignIn client={client} redirectTo={redirectTo} recovery onRecovered={() => setRecovery(false)} />;
  if (loadError) return <main className="centered card auth"><Brand /><p role="alert">{loadError}</p><span className="row"><button onClick={() => void loadOrgs()}>Retry</button><button className="link" onClick={() => void client.auth.signOut()}>Sign out</button></span></main>;
  if (!orgs) return <main className="centered">Loading…</main>;

  // A signed-in account that belongs to no organization is either the first person ever to
  // arrive, or a Google identity the identity service did not link to the owner's account.
  // Only the second is dangerous, and only the door the session came through tells them
  // apart - so it is read from what the identity service recorded, and decided in lib/auth.
  const decision = entryDecision({
    organizationCount: orgs.length,
    method: signInMethod(session.user),
  });

  if (decision === 'unlinked_oauth') {
    const leave = (notice: string | null) => {
      setSignInNotice(notice);
      void client.auth.signOut();
    };
    return (
      <UnlinkedOAuthAccount
        email={signedInEmail(session.user)}
        onSignOut={() => leave(null)}
        onUseEmailSignIn={() => leave(
          'Sign in with the email address and password your owner account uses. The Google account you tried is not linked to it.',
        )}
      />
    );
  }

  return (
    <>
      {decision === 'bootstrap'
        ? <Bootstrap client={client} session={session} guard={guard} onCreated={() => void loadOrgs()} />
        : <OperationsRoom client={client} reader={reader} orgs={orgs} guard={guard} owner={{ verified, verify }}
            onSignOut={() => void client.auth.signOut()} nodeConnection={nodeConnection} plannerUrl={plannerUrl}
            accountEmail={session.user.email ?? null} />}
      {dialog}
    </>
  );
}
